import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Stop } from '../constants/stopsData'
import { getLoadsByTagAndLoad } from '../services/loadService'
import type { LoadFormData } from '../types/storageTypes'
import AddStopModal from './AddStopModal'
import { DurationSelector } from './DurationSelector'
import SelectedStops from './SelectedStops'

interface ScaleInfoSectionProps {
  formData: LoadFormData
  setFormData: React.Dispatch<React.SetStateAction<LoadFormData>>
  selectedStops: Stop[]
  setSelectedStops: React.Dispatch<React.SetStateAction<Stop[]>>
  tagId?: string
  loadNumber?: string
  isFinal?: boolean
}

const ScaleInfoSection: React.FC<ScaleInfoSectionProps> = ({
  formData,
  setFormData,
  selectedStops,
  setSelectedStops,
  tagId,
  loadNumber,
  isFinal = false,
}) => {
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Load stored stops on mount
  useEffect(() => {
    const loadData = async () => {
      // First check localStorage
      const localScaleTagId = localStorage.getItem(
        `scale_scaleTagId_${tagId}_${loadNumber}`,
      )
      const localWeight = localStorage.getItem(
        `scale_weight_${tagId}_${loadNumber}`,
      )
      const localTolls = localStorage.getItem(
        `scale_tolls_${tagId}_${loadNumber}`,
      )
      const localStops = localStorage.getItem(
        `scale_selectedStops_${tagId}_${loadNumber}`,
      )

      // If we have any data in localStorage, use it and don't fetch
      if (localScaleTagId || localWeight || localTolls || localStops) {
        setFormData((prev) => ({
          ...prev,
          scaleTagId: localScaleTagId || prev.scaleTagId,
          weight: localWeight || prev.weight,
          tolls: localTolls || prev.tolls,
        }))
        if (localStops) {
          setSelectedStops(JSON.parse(localStops))
        }
        return
      }

      // If no localStorage data, fetch from API
      if (tagId && loadNumber) {
        try {
          const loadData = await getLoadsByTagAndLoad(tagId, loadNumber)
          if (loadData) {
            setFormData((prev) => ({
              ...prev,
              scaleTagId: loadData.scale_tag_id || '',
              weight: loadData.weight?.toString() || '',
              tolls: loadData.tolls?.toString() || '',
            }))
            if (loadData.stops) {
              setSelectedStops(loadData.stops)
            }

            // Store in localStorage after fetching
            localStorage.setItem(
              `scale_scaleTagId_${tagId}_${loadNumber}`,
              loadData.scale_tag_id || '',
            )
            localStorage.setItem(
              `scale_weight_${tagId}_${loadNumber}`,
              loadData.weight?.toString() || '',
            )
            localStorage.setItem(
              `scale_tolls_${tagId}_${loadNumber}`,
              loadData.tolls?.toString() || '',
            )
            if (loadData.stops) {
              localStorage.setItem(
                `scale_selectedStops_${tagId}_${loadNumber}`,
                JSON.stringify(loadData.stops),
              )
            }
          }
        } catch (error) {
          console.error('Failed to load data:', error)
        }
      }
    }

    loadData()
  }, [tagId, loadNumber, setFormData, setSelectedStops])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isFinal) return
    const { name, value } = e.target

    // For weight field, only allow numeric values and decimal points
    if (name === 'weight' && value !== '') {
      const numericValue = value.replace(/[^0-9.,]/g, '')
      if (numericValue !== value) return
    }

    localStorage.setItem(`scale_${name}_${tagId}_${loadNumber}`, value)
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleDurationChange = (stopId: string, value: string) => {
    if (isFinal) return
    if (!tagId) return // Add guard clause

    localStorage.setItem(
      `scale_duration_${tagId}_${loadNumber}_${stopId}`,
      value,
    )
    setFormData((prev) => ({
      ...prev,
      durations: {
        ...prev.durations,
        [stopId]: value,
      },
    }))
  }

  const handleAddStop = (newStop: Stop) => {
    // Add to selected stops
    setSelectedStops((prev) => [...prev, newStop])
    localStorage.setItem(
      `scale_selectedStops_${tagId}_${loadNumber}`,
      JSON.stringify([...selectedStops, newStop]),
    )

    // Only update durations if duration exists
    if (newStop.duration) {
      localStorage.setItem(
        `scale_duration_${tagId}_${loadNumber}_${newStop.id}`,
        newStop.duration,
      )

      setFormData((prev) => ({
        ...prev,
        durations: {
          ...(prev.durations || {}),
          [newStop.id]: newStop.duration || '', // Ensure string type
        },
      }))
    }
  }

  const handleDeleteStop = (stopId: string) => {
    // Remove from selected stops
    setSelectedStops((prev) => prev.filter((stop) => stop.id !== stopId))

    // Remove from localStorage
    const updatedStops = selectedStops.filter((stop) => stop.id !== stopId)
    localStorage.setItem(
      `scale_selectedStops_${tagId}_${loadNumber}`,
      JSON.stringify(updatedStops),
    )

    // Remove duration
    setFormData((prev) => ({
      ...prev,
      durations: Object.fromEntries(
        Object.entries(prev.durations || {}).filter(([id]) => id !== stopId),
      ),
    }))
    localStorage.removeItem(`scale_duration_${tagId}_${loadNumber}_${stopId}`)
  }

  return (
    <div className={css(styles.formContainer)}>
      <div className={css(styles.loadButtons)}>
        <div className={css(styles.inputGroup)}>
          <label className={css(styles.label)}>Scale ID</label>
          <div className={css(styles.inputWrapper)}>
            <input
              name="scaleTagId"
              value={formData.scaleTagId}
              onChange={handleInputChange}
              className={css(styles.input, isFinal && styles.inputDisabled)}
              placeholder=""
              disabled={isFinal}
              maxLength={12}
            />
          </div>
        </div>

        <div className={css(styles.inputGroup)}>
          <label className={css(styles.label)}>Weight (Tons)</label>
          <div className={css(styles.inputWrapper)}>
            <input
              name="weight"
              value={formData.weight}
              onChange={handleInputChange}
              className={css(styles.input, isFinal && styles.inputDisabled)}
              placeholder=""
              type="number"
              step="0.01"
              min="0"
              disabled={isFinal}
              maxLength={5}
            />
          </div>
        </div>

        <div className={css(styles.inputGroup)}>
          <label className={css(styles.label)}>Tolls</label>
          <div className={css(styles.inputWrapper)}>
            <span className={css(styles.dollarSign)}>$</span>
            <input
              name="tolls"
              value={formData.tolls}
              onChange={handleInputChange}
              className={css(styles.input, styles.tollsInput)}
              type="number"
              step="0.01"
              min="0"
              placeholder=""
              disabled={isFinal}
              maxLength={5}
            />
          </div>
        </div>
      </div>

      {selectedStops.length > 0 && (
        <SelectedStops
          stops={selectedStops}
          durations={formData.durations || {}}
          onDurationChange={handleDurationChange}
          onDeleteStop={handleDeleteStop}
          disabled={isFinal}
        />
      )}

      <div className={css(styles.actionButtons)}>
        <button
          className={css(styles.stopsButton, isFinal && styles.buttonDisabled)}
          onClick={() => !isFinal && setIsModalOpen(true)}
          disabled={isFinal}
        >
          <i className={`fa-solid fa-circle-plus ${css(styles.icon)}`} />
          Add Delay
        </button>
      </div>

      <AddStopModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleAddStop}
      />
    </div>
  )
}

const styles = StyleSheet.create({
  formContainer: {
    width: '90%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  loadButtons: {
    display: 'grid',
    gridTemplateColumns: '1.5fr 1fr 1fr', // Scale Tag takes 1.5, Weight and Tolls take 1 each
    gap: '16px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '16px',
  },
  label: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#111928',
    lineHeight: '150%',
    fontFamily: 'Inter',
  },
  input: {
    padding: '0px 20px',
    background: '#F3F4F6',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    height: '42px',
    fontSize: '12px',
    fontFamily: 'Inter',
    width: '100%',
    boxSizing: 'border-box',
  },
  inputDisabled: {
    backgroundColor: '#E5E7EB',
    cursor: 'not-allowed',
    color: '#6B7280',
    ':hover': {
      borderColor: '#E5E7EB',
    },
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
    backgroundColor: '#F3F4F6',
    borderColor: '#E5E7EB',
    color: '#6B7280',
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: '20px',
  },
  stopsButton: {
    backgroundColor: '#FFFFFF',
    color: '#007BFF',
    padding: '10px 20px',
    border: '2px solid #007BFF',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    transition: 'background-color 0.3s ease',
    width: '100%',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '-20px',
    gap: 6,
  },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
  },
  inputWrapper: {
    position: 'relative',
  },
  dollarSign: {
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#374151',
    fontSize: '12px',
    pointerEvents: 'none',
    zIndex: 1,
  },
  tollsInput: {
    paddingLeft: '24px !important',
  },
})

export default ScaleInfoSection
